import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { AuthModalField } from '@/components/auth/AuthModalField'
import { AuthPrimaryButton } from '@/components/auth/AuthPrimaryButton'
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons'
import { useAuthModal } from '@/context/AuthModalContext'
import { useLoader } from '@/context/LoaderContext'
import { loginSchema, type LoginFormValues } from '@/lib/auth-validation'
import { useLoginMutation } from '@/store/api/authApi'

export function LoginForm() {
	const { close, requireUsername, setView } = useAuthModal()
	const { showLoader, hideLoader } = useLoader()
	const [login, { isLoading }] = useLoginMutation()

	useEffect(() => {
		if (!isLoading) {
			return
		}

		showLoader()
		return () => hideLoader()
	}, [isLoading, showLoader, hideLoader])

	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<LoginFormValues>({
		resolver: zodResolver(loginSchema),
		defaultValues: {
			email: '',
			password: '',
		},
	})

	const onSubmit = async (values: LoginFormValues) => {
		try {
			const response = await login({
				email: values.email,
				password: values.password,
			}).unwrap()
			if (response.data.has_username === false) {
				requireUsername()
			} else {
				close()
			}
		} catch {
			// Error toast is shown globally by the API interceptor.
		}
	}

	return (
		<form noValidate className='flex flex-col gap-4' onSubmit={handleSubmit(onSubmit)}>
			<AuthModalField type='email' placeholder='Email address' autoComplete='email' error={errors.email?.message} {...register('email')} />
			<AuthModalField type='password' placeholder='Password' autoComplete='current-password' error={errors.password?.message} {...register('password')} />

			<div className='flex justify-end'>
				<button
					type='button'
					onClick={() => setView('forgot-password')}
					className='text-sm font-medium tracking-[-0.21px] text-foreground transition-opacity hover:opacity-80'
				>
					Forgot password?
				</button>
			</div>

			<AuthPrimaryButton label='Login' type='submit' isLoading={isLoading} />

			<div className='mt-3 flex items-center gap-2'>
				<span className='h-px flex-1 bg-border' />
				<span className='text-sm font-medium tracking-[-0.21px] text-subtle-foreground'>OR</span>
				<span className='h-px flex-1 bg-border' />
			</div>

			<SocialLoginButtons />

			<p className='mt-4 text-center text-xs leading-[140%] tracking-[-0.18px] text-subtle-foreground'>
				By accessing you confirm that you are at least 18 years old and agree to the{' '}
				<button type='button' className='focuse-visible-with-primary:text-secondary underline underline-offset-2 transition-all hover:text-primary'>
					<span className='focus-visible:text-secondary'>Terms of service</span>
				</button>
			</p>
		</form>
	)
}
